import { useMemo, useState } from "react";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  type ColumnDef,
  type FilterFn,
  type SortingState,
  type Table as TanStackTable,
  useReactTable,
} from "@tanstack/react-table";
import { type TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ArrowDownUp, Search, X } from "lucide-react";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { WFPButton } from "@/components/ui/button";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@/components/ui/input-group";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SortableColumnHeader, getColumnSortAria } from "@/components/ui/sortable-column-header";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  QuickFilterCards,
  QuickFilterCardsSkeleton,
  getQuickFilter,
  type QuickFilterGroup,
} from "@/components/QuickFilterCards";
import { RelativeDateCell } from "@/components/RelativeDateCell";
import {
  formatDateCompact,
  formatDateTimeCompact,
  formatRelativeDate,
  formatRelativeDateTime,
} from "../../utils/relative-time";
import { formatSyncEventLabel } from "../../utils/sync-event-label";
import { type AggregatorAdmin } from "../Aggregators/components/AdminsTable/aggregator-admins-response";
import { ADMINISTRATOR_SORT_OPTIONS, VERSION_STATUS_META, getAggregatorDetailPath } from "./config";
import {
  ADMINISTRATOR_QUICK_FILTERS,
  countAdministratorQuickFilters,
  type QuickFilterId,
} from "./quick-filters";

interface AdministratorColumnMeta {
  cellClassName?: string;
  headerClassName?: string;
}

type AdministratorColumnDef = ColumnDef<AggregatorAdmin, unknown> & {
  meta?: AdministratorColumnMeta;
};

const EMPTY_VALUE = "-";

const SKELETON_ROW_COUNT = 8;

const SORTING_MENU_ITEM_CLASS =
  "data-[state=checked]:bg-primary-50 data-[state=checked]:font-medium data-[state=checked]:text-primary-700 focus:bg-primary-100 focus:text-primary-700";

/** Badge tints match the quick filter card the active filter was pressed on. */
const QUICK_FILTER_BADGE_VARIANT = {
  healthy: "success",
  warning: "warning",
  attention: "destructive",
} as const satisfies Record<QuickFilterGroup, string>;

const getColumnMeta = (columnDef: ColumnDef<AggregatorAdmin, unknown>) =>
  columnDef.meta as AdministratorColumnMeta | undefined;

const getAdministratorFullName = (administrator: AggregatorAdmin) => {
  const person = administrator.person;
  const fullName = person?.full_name?.trim();

  if (fullName) {
    return fullName;
  }

  return [person?.first_name, person?.last_name]
    .filter((name): name is string => Boolean(name?.trim()))
    .join(" ")
    .trim();
};

const getAggregatorName = (administrator: AggregatorAdmin) =>
  administrator.aggregator?.name?.trim() || "";

const getAggregatorId = (administrator: AggregatorAdmin) => administrator.aggregator?.id;

const getAdministratorPhone = (administrator: AggregatorAdmin) =>
  administrator.person?.contact_phone?.trim() || "";

const getAdministratorEmail = (administrator: AggregatorAdmin) =>
  administrator.person?.contact_email?.trim() || "";

const getAdministratorLastSyncDate = (administrator: AggregatorAdmin) =>
  administrator.last_synchronization_log?.synchronization_date ?? "";

const getAdministratorUpdatedOnDate = (administrator: AggregatorAdmin) =>
  administrator.updated_on ?? "";

const getAdministratorVersionStatus = (administrator: AggregatorAdmin) =>
  administrator.version_status?.trim() ?? "";

const getAdministratorAppVersion = (administrator: AggregatorAdmin) => {
  const buildVersion = administrator.last_synchronization_log?.device_information?.buildVersion;

  return typeof buildVersion === "string" ? buildVersion.trim() : "";
};

const getAdministratorDeviceModel = (administrator: AggregatorAdmin) => {
  const model = administrator.last_synchronization_log?.device_information?.model;

  return typeof model === "string" ? model.trim() : "";
};

const getAdministratorFirstLogin = (administrator: AggregatorAdmin) =>
  formatDateTimeCompact(administrator.first_login);

const formatValue = (value: string) => value || EMPTY_VALUE;

/** Colored dot + label reflecting the backend mobile-version classification. */
const VersionStatusIndicator = ({ status, t }: { status: string; t: TFunction }) => {
  const meta = VERSION_STATUS_META[status];

  if (!meta) {
    return null;
  }

  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
      <span className={cn("size-2 shrink-0 rounded-full", meta.dotClassName)} aria-hidden="true" />
      {t(meta.translationKey)}
    </span>
  );
};

const getAdministratorRowId = (administrator: AggregatorAdmin, index: number) => {
  if (administrator.id !== undefined && administrator.id !== null) {
    return String(administrator.id);
  }

  return (
    getAdministratorEmail(administrator) ||
    getAdministratorPhone(administrator) ||
    `${getAdministratorFullName(administrator)}-${index}`
  );
};

const searchFilter: FilterFn<AggregatorAdmin> = (row, _columnId, filterValue) => {
  const term = String(filterValue).trim().toLowerCase();

  if (!term) {
    return true;
  }

  const administrator = row.original;

  return [
    getAdministratorFullName(administrator),
    getAggregatorName(administrator),
    getAdministratorEmail(administrator),
  ].some(field => field.toLowerCase().includes(term));
};

const createColumns = (t: TFunction): AdministratorColumnDef[] => [
  {
    id: "aggregator",
    accessorFn: getAggregatorName,
    header: ({ column }) => (
      <SortableColumnHeader
        column={column}
        label={t("home.manager.overview.administratorsModal.columns.aggregator")}
      />
    ),
    cell: ({ row }) => {
      const administrator = row.original;
      const aggregatorId = getAggregatorId(administrator);
      const aggregatorName = getAggregatorName(administrator);

      if (aggregatorId === undefined || aggregatorId === null || !aggregatorName) {
        return formatValue(aggregatorName);
      }

      return (
        <Link
          to={getAggregatorDetailPath(aggregatorId)}
          className="font-medium text-primary-600 hover:text-primary-700 hover:underline"
        >
          {aggregatorName}
        </Link>
      );
    },
    meta: {
      cellClassName: "w-56 whitespace-normal leading-5",
      headerClassName: "w-56",
    },
  },
  {
    id: "name",
    accessorFn: getAdministratorFullName,
    header: ({ column }) => (
      <SortableColumnHeader
        column={column}
        label={t("home.manager.overview.administratorsModal.columns.fullName")}
      />
    ),
    cell: ({ row }) => (
      <span className="font-medium text-foreground">
        {formatValue(getAdministratorFullName(row.original))}
      </span>
    ),
    meta: {
      cellClassName: "w-48 whitespace-normal leading-5",
      headerClassName: "w-48",
    },
  },
  {
    id: "email",
    accessorFn: getAdministratorEmail,
    header: ({ column }) => (
      <SortableColumnHeader
        column={column}
        label={t("home.manager.overview.administratorsModal.columns.email")}
      />
    ),
    cell: ({ row }) => formatValue(getAdministratorEmail(row.original)),
    meta: {
      cellClassName: "w-64 whitespace-normal break-words leading-5 text-muted-foreground",
      headerClassName: "w-64",
    },
  },
  {
    id: "mobile",
    accessorFn: getAdministratorPhone,
    header: () => t("home.manager.overview.administratorsModal.columns.mobilePhone"),
    enableSorting: false,
    cell: ({ row }) => formatValue(getAdministratorPhone(row.original)),
    meta: {
      cellClassName: "w-40",
      headerClassName: "w-40",
    },
  },
  {
    id: "firstLogin",
    accessorFn: administrator => administrator.first_login ?? "",
    header: ({ column }) => (
      <SortableColumnHeader
        column={column}
        label={t("home.manager.overview.administratorsModal.columns.firstLogin")}
      />
    ),
    cell: ({ row }) => formatValue(getAdministratorFirstLogin(row.original)),
    meta: {
      cellClassName: "w-44 text-muted-foreground",
      headerClassName: "w-44",
    },
  },
  {
    id: "lastSync",
    accessorFn: getAdministratorLastSyncDate,
    header: ({ column }) => (
      <SortableColumnHeader
        column={column}
        label={t("home.manager.overview.administratorsModal.columns.lastSync")}
      />
    ),
    cell: ({ row }) => {
      const value = getAdministratorLastSyncDate(row.original);

      return (
        <RelativeDateCell
          relative={formatRelativeDateTime(value, t)}
          absolute={formatDateTimeCompact(value)}
          caption={formatSyncEventLabel(row.original.last_synchronization_log?.event)}
        />
      );
    },
    meta: {
      cellClassName: "w-44",
      headerClassName: "w-44",
    },
  },
  {
    id: "updatedOn",
    accessorFn: getAdministratorUpdatedOnDate,
    header: ({ column }) => (
      <SortableColumnHeader
        column={column}
        label={t("home.manager.overview.administratorsModal.columns.updatedOn")}
      />
    ),
    cell: ({ row }) => {
      const value = getAdministratorUpdatedOnDate(row.original);

      return (
        <RelativeDateCell
          relative={formatRelativeDate(value, t)}
          absolute={formatDateCompact(value)}
        />
      );
    },
    meta: {
      cellClassName: "w-40",
      headerClassName: "w-40",
    },
  },
  {
    id: "appVersion",
    accessorFn: getAdministratorAppVersion,
    header: ({ column }) => (
      <SortableColumnHeader
        column={column}
        label={t("home.manager.overview.administratorsModal.columns.mobileAppVersion")}
      />
    ),
    cell: ({ row }) => (
      <div className="flex flex-col gap-1">
        <span className="text-foreground">
          {formatValue(getAdministratorAppVersion(row.original))}
        </span>
        <VersionStatusIndicator status={getAdministratorVersionStatus(row.original)} t={t} />
      </div>
    ),
    meta: {
      cellClassName: "w-40 text-muted-foreground",
      headerClassName: "w-40",
    },
  },
  {
    id: "deviceModel",
    accessorFn: getAdministratorDeviceModel,
    header: ({ column }) => (
      <SortableColumnHeader
        column={column}
        label={t("home.manager.overview.administratorsModal.columns.deviceModel")}
      />
    ),
    cell: ({ row }) => formatValue(getAdministratorDeviceModel(row.original)),
    meta: {
      cellClassName: "w-40 text-muted-foreground",
      headerClassName: "w-40",
    },
  },
];

const AdministratorsDataTable = ({
  emptyMessage,
  isLoading,
  table,
}: {
  emptyMessage: string;
  isLoading: boolean;
  table: TanStackTable<AggregatorAdmin>;
}) => {
  const rows = table.getRowModel().rows;
  const columnCount = table.getVisibleLeafColumns().length;

  return (
    <Table>
      <TableHeader>
        {table.getHeaderGroups().map(headerGroup => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map(header => (
              <TableHead
                key={header.id}
                className={getColumnMeta(header.column.columnDef)?.headerClassName}
                aria-sort={getColumnSortAria(header.column)}
              >
                {header.isPlaceholder
                  ? null
                  : flexRender(header.column.columnDef.header, header.getContext())}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {isLoading
          ? Array.from({ length: SKELETON_ROW_COUNT }, (_, rowIndex) => (
              <TableRow key={`skeleton-${rowIndex}`}>
                {table.getVisibleLeafColumns().map(column => (
                  <TableCell
                    key={column.id}
                    className={getColumnMeta(column.columnDef)?.cellClassName}
                  >
                    <Skeleton className="h-4 w-full" />
                  </TableCell>
                ))}
              </TableRow>
            ))
          : null}
        {!isLoading && rows.length === 0 ? (
          <TableRow>
            <TableCell colSpan={columnCount} className="h-24 text-center text-muted-foreground">
              {emptyMessage}
            </TableCell>
          </TableRow>
        ) : null}
        {!isLoading && rows.length
          ? rows.map(row => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map(cell => (
                  <TableCell
                    key={cell.id}
                    className={getColumnMeta(cell.column.columnDef)?.cellClassName}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          : null}
      </TableBody>
    </Table>
  );
};

interface AdministratorsToolbarProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  sorting: SortingState;
  onSortColumnChange: (columnId: string) => void;
  onSortDirectionChange: (direction: string) => void;
  quickFilterLabel?: string;
  quickFilterGroup?: QuickFilterGroup;
  onClearQuickFilter: () => void;
}

const AdministratorsToolbar = ({
  searchTerm,
  onSearchChange,
  sorting,
  onSortColumnChange,
  onSortDirectionChange,
  quickFilterLabel,
  quickFilterGroup,
  onClearQuickFilter,
}: AdministratorsToolbarProps) => {
  const { t } = useTranslation();
  const selectedSort = sorting[0];
  const selectedSortOption = ADMINISTRATOR_SORT_OPTIONS.find(
    sortOption => sortOption.id === selectedSort?.id
  );
  const selectedSortColumn = selectedSortOption?.id ?? "";
  const selectedSortDirection = selectedSort?.desc ? "desc" : "asc";
  const sortButtonLabel = selectedSortOption
    ? t(selectedSortOption.translationKey)
    : t("home.manager.overview.administratorsModal.sort.button");

  return (
    <div className="flex flex-wrap items-center gap-2">
      <InputGroup className="w-full max-w-xs sm:w-80">
        <InputGroupAddon>
          <Search className="size-4" aria-hidden="true" />
        </InputGroupAddon>
        <InputGroupInput
          type="search"
          value={searchTerm}
          className="[&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden"
          placeholder={t("home.manager.overview.administratorsModal.searchPlaceholder")}
          aria-label={t("home.manager.overview.administratorsModal.searchPlaceholder")}
          onChange={event => onSearchChange(event.target.value)}
        />
        {searchTerm ? (
          <InputGroupAddon align="inline-end">
            <InputGroupButton
              type="button"
              variant="ghost"
              size="icon-xs"
              aria-label={t("home.manager.overview.administratorsModal.clearSearch")}
              onClick={() => onSearchChange("")}
            >
              <X className="size-3.5" aria-hidden="true" />
            </InputGroupButton>
          </InputGroupAddon>
        ) : null}
      </InputGroup>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <WFPButton type="button" variant="outline">
            <ArrowDownUp className="size-4" aria-hidden="true" />
            {sortButtonLabel}
          </WFPButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56">
          <DropdownMenuLabel>
            {t("home.manager.overview.administratorsModal.sort.sortBy")}
          </DropdownMenuLabel>
          <DropdownMenuRadioGroup value={selectedSortColumn} onValueChange={onSortColumnChange}>
            {ADMINISTRATOR_SORT_OPTIONS.map(sortOption => (
              <DropdownMenuRadioItem
                key={sortOption.id}
                value={sortOption.id}
                indicator="check"
                className={SORTING_MENU_ITEM_CLASS}
              >
                {t(sortOption.translationKey)}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
          <DropdownMenuSeparator />
          <DropdownMenuLabel>
            {t("home.manager.overview.administratorsModal.sort.order")}
          </DropdownMenuLabel>
          <DropdownMenuRadioGroup
            value={selectedSortDirection}
            onValueChange={onSortDirectionChange}
          >
            <DropdownMenuRadioItem
              value="asc"
              indicator="check"
              className={SORTING_MENU_ITEM_CLASS}
            >
              {t("home.manager.overview.administratorsModal.sort.ascending")}
            </DropdownMenuRadioItem>
            <DropdownMenuRadioItem
              value="desc"
              indicator="check"
              className={SORTING_MENU_ITEM_CLASS}
            >
              {t("home.manager.overview.administratorsModal.sort.descending")}
            </DropdownMenuRadioItem>
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>

      {quickFilterLabel ? (
        <Badge
          variant={quickFilterGroup ? QUICK_FILTER_BADGE_VARIANT[quickFilterGroup] : "default"}
          className="gap-1.5 py-1 pr-1 pl-2.5"
        >
          {quickFilterLabel}
          <button
            type="button"
            aria-label={t("home.manager.overview.administratorsModal.quickFilters.clear")}
            onClick={onClearQuickFilter}
            className="inline-flex cursor-pointer items-center justify-center rounded-full p-0.5 hover:bg-neutral-alpha-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X aria-hidden="true" size={16} />
          </button>
        </Badge>
      ) : null}
    </div>
  );
};

interface AdministratorsTableProps {
  isFetchingAdministrators?: boolean;
  administrators?: AggregatorAdmin[];
  /** Renders the pressable filter cards above the toolbar. Off on the aggregator profile page. */
  showQuickFilters?: boolean;
  /** Live mobile app version, named by the "using latest version" filter label. */
}

export function AdministratorsTable({
  isFetchingAdministrators = false,
  administrators = [],
  showQuickFilters = false,
}: AdministratorsTableProps) {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [sorting, setSorting] = useState<SortingState>([]);
  const [quickFilter, setQuickFilter] = useState<QuickFilterId | null>(null);

  const columns = useMemo(() => createColumns(t), [t]);

  // The list is fetched whole and paged client-side, so narrowing the array before
  // it reaches the table is enough - no extra TanStack filter needed.
  const rows = useMemo(() => {
    const filter = getQuickFilter(ADMINISTRATOR_QUICK_FILTERS, quickFilter);

    return filter ? administrators.filter(filter.matches) : administrators;
  }, [administrators, quickFilter]);

  const quickFilterCounts = useMemo(
    () => countAdministratorQuickFilters(administrators),
    [administrators]
  );

  const activeQuickFilter = getQuickFilter(ADMINISTRATOR_QUICK_FILTERS, quickFilter);
  const quickFilterLabel = activeQuickFilter ? t(activeQuickFilter.labelKey) : undefined;

  const table = useReactTable({
    columns,
    data: rows,
    enableMultiSort: false,
    enableSortingRemoval: false,
    globalFilterFn: searchFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getRowId: getAdministratorRowId,
    getSortedRowModel: getSortedRowModel(),
    onGlobalFilterChange: setSearchTerm,
    onSortingChange: setSorting,
    state: {
      globalFilter: searchTerm,
      sorting,
    },
  });

  const onSortColumnChange = (columnId: string) => {
    setSorting([{ id: columnId, desc: sorting[0]?.desc ?? false }]);
  };

  const onSortDirectionChange = (direction: string) => {
    setSorting([
      { id: sorting[0]?.id ?? ADMINISTRATOR_SORT_OPTIONS[0].id, desc: direction === "desc" },
    ]);
  };

  return (
    <div className="flex flex-col gap-4">
      {showQuickFilters && isFetchingAdministrators ? (
        <QuickFilterCardsSkeleton filters={ADMINISTRATOR_QUICK_FILTERS} />
      ) : null}
      {showQuickFilters && !isFetchingAdministrators ? (
        <QuickFilterCards
          counts={quickFilterCounts}
          filters={ADMINISTRATOR_QUICK_FILTERS}
          ariaLabel={t("home.manager.overview.administratorsModal.quickFilters.ariaLabel")}
          value={quickFilter}
          onChange={setQuickFilter}
        />
      ) : null}
      <AdministratorsToolbar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        sorting={sorting}
        onSortColumnChange={onSortColumnChange}
        onSortDirectionChange={onSortDirectionChange}
        quickFilterLabel={quickFilterLabel}
        quickFilterGroup={activeQuickFilter?.group}
        onClearQuickFilter={() => setQuickFilter(null)}
      />
      <div className="overflow-x-auto rounded-md border border-border bg-background">
        <AdministratorsDataTable
          emptyMessage={t("home.manager.overview.administratorsModal.empty")}
          isLoading={isFetchingAdministrators}
          table={table}
        />
      </div>
    </div>
  );
}
